import { FilterIcon, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { SortByIcon } from "../siteIcons";
import './FilterSidebar.css';
import styles from './FilterSidebar.module.css';
import { FILTER_CONFIG } from "./filterconfig";



// Skeleton component for loading state
const FilterSkeleton = ({ filterType }) => {
    // Default to an empty object if FILTER_CONFIG[filterType] is undefined or null
    const filterConfig = FILTER_CONFIG[filterType] || {};

    return (
        <div className="bg-white w-100 p-3 rounded max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="mb-4">
                <div className="skeleton skeleton-title w-50 mb-2" style={{ height: "1.25rem" }}></div>
                <ul className="list-unstyled">
                    {[...Array(4)].map((_, index) => (
                        <li key={index} className="form-check mb-1">
                            <div className="skeleton skeleton-checkbox" style={{ height: "1rem", width: "1rem", display: "inline-block", marginRight: "0.5rem" }}></div>
                            <div className="skeleton skeleton-text w-75" style={{ height: "1rem", display: "inline-block" }}></div>
                        </li>
                    ))}
                </ul>
            </div>

            {Object.values(filterConfig).map((config, index) => (
                <div key={index} className="mb-4">
                    <div className="skeleton skeleton-title w-50 mb-2" style={{ height: "1.25rem" }}></div>
                    <ul className="list-unstyled">
                        {[...Array(6)].map((_, i) => (
                            <li key={i} className="form-check mb-1">
                                <div className="skeleton skeleton-checkbox" style={{ height: "1rem", width: "1rem", display: "inline-block", marginRight: "0.5rem" }}></div>
                                <div className="skeleton skeleton-text w-75" style={{ height: "1rem", display: "inline-block" }}></div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            <div className="bg-white p-3 border-top">
                <div className="skeleton skeleton-button w-100" style={{ height: "2.5rem", borderRadius: "0.25rem" }}></div>
            </div>
        </div>
    );
};

const FilterContent = ({ filterData, filters, setFilters, isMobile = false, setOpen, onReset, filterType }) => {
    const [showAllStates, setShowAllStates] = useState({});
    // Price range setup
    const priceRanges = filterData?.price_range_initial?.ranges || [];

    // State for selected price range (single object or null)
    const [selectedPriceRange, setSelectedPriceRange] = useState(filters?.priceRange ?? null);

    const handleReset = () => {
        if (onReset) {
            onReset();
        }
        setFilters({
            priceRange: null,
            colors: [],
            brands: [],
            categories: [],
            subcategories: [],
        });
        setSelectedPriceRange(null);
    };

    const handleApply = () => {
        if (isMobile && setOpen) {
            setOpen(false);
        }
    };

    const hasActiveFilters =
        selectedPriceRange !== null ||
        filters?.colors?.length > 0 ||
        filters?.brands?.length > 0 ||
        filters?.categories?.length > 0;

    const getSortedOptions = (options, selectedIds) => {
        if (!options) return [];
        return [...options].sort((a, b) => {
            const aSelected = selectedIds?.includes(a.id);
            const bSelected = selectedIds?.includes(b.id);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
        });
    };

    const renderFilterSection = (config) => {
        // Return null if config is invalid
        if (!config || !config.accessor || !config.title || !config.stateKey) {
            return null;
        }

        // Safely access options
        const options = filterData?.[config.accessor];

        // Return null if options is not an array or is empty
        if (!Array.isArray(options) || options.length === 0) {
            return null;
        }

        if (config.accessor === "categories") {
            const categoryOptions = options;
            const displayedCategories = showAllStates[config.accessor]
                ? categoryOptions
                : categoryOptions.slice(0, 6);

            return (
                <div className="mb-4">
                    <h3 className="fw-bold mb-2 fs-6">{config.title}</h3>
                    <ul className="list-unstyled">
                        {displayedCategories.map((category) => {
                            // Ensure category is valid
                            if (!category || !category.id || !category.name) {
                                return null;
                            }

                            const hasSubcategories = Array.isArray(category.subcategories) && category.subcategories.length > 0;
                            const allSubcategoriesSelected =
                                hasSubcategories &&
                                category.subcategories.every((sub) => filters.subcategories?.includes(sub?.id));

                            return (
                                <li key={category.id} className="mb-2">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={filters.categories?.includes(category.id) || allSubcategoriesSelected}
                                            onChange={(e) => {
                                                if (hasSubcategories) {
                                                    const subcategoryIds = category.subcategories?.map((sub) => sub?.id).filter(Boolean) || [];
                                                    const newSubcategories = e.target.checked
                                                        ? [...new Set([...(filters.subcategories || []), ...subcategoryIds])]
                                                        : (filters.subcategories || []).filter((id) => !subcategoryIds.includes(id));
                                                    handleFilterChange({ subcategories: newSubcategories });
                                                } else {
                                                    const newCategories = e.target.checked
                                                        ? [...new Set([...(filters.categories || []), category.id])]
                                                        : (filters.categories || []).filter((id) => id !== category.id);
                                                    handleFilterChange({ categories: newCategories });
                                                }
                                            }}
                                            id={`category-${category.id}`}
                                        />
                                        <label className="form-check-label" htmlFor={`category-${category.id}`}>
                                            {category.name} <span className="text-muted">({category.count || 0})</span>
                                        </label>
                                    </div>

                                    {hasSubcategories && (
                                        <ul className="list-unstyled ps-4 mt-2">
                                            {category.subcategories.map((subcategory) => {
                                                // Ensure subcategory is valid
                                                if (!subcategory || !subcategory.id || !subcategory.name) {
                                                    return null;
                                                }

                                                return (
                                                    <li key={subcategory.id} className="form-check mb-1">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={filters.subcategories?.includes(subcategory.id)}
                                                            onChange={(e) => {
                                                                const newSubcategories = e.target.checked
                                                                    ? [...new Set([...(filters.subcategories || []), subcategory.id])]
                                                                    : (filters.subcategories || []).filter((id) => id !== subcategory.id);
                                                                handleFilterChange({ subcategories: newSubcategories });
                                                            }}
                                                            id={`subcategory-${subcategory.id}`}
                                                        />
                                                        <label className="form-check-label" htmlFor={`subcategory-${subcategory.id}`}>
                                                            {subcategory.name} <span className="text-muted">({subcategory.count || 0})</span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {categoryOptions.length > 6 && (
                        <button
                            type="button"
                            className="btn btn-link text-primary p-0 mt-2"
                            onClick={() =>
                                setShowAllStates((prev) => ({
                                    ...prev,
                                    [config.accessor]: !prev[config.accessor],
                                }))
                            }
                        >
                            {showAllStates[config.accessor] ? (
                                <>
                                    Show Less <i className="bi bi-chevron-up ms-1"></i>
                                </>
                            ) : (
                                <>
                                    Show More <i className="bi bi-chevron-down ms-1"></i>
                                </>
                            )}
                        </button>
                    )}
                </div>
            );
        }

        const sortedOptions = getSortedOptions(options, filters?.[config?.stateKey] || []);
        const displayedOptions = showAllStates[config.accessor]
            ? sortedOptions
            : sortedOptions.slice(0, 6);

        return (
            <div className="mb-4">
                <h3 className="fw-bold mb-2 fs-6">{config.title}</h3>
                <ul className="list-unstyled">
                    {displayedOptions.map((option) => {
                        // Ensure option is valid
                        if (!option || !option.id || !option.name) {
                            return null;
                        }

                        return (
                            <li key={option.id} className="form-check mb-1">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={filters?.[config.stateKey]?.includes(option.id)}
                                    onChange={(e) => {
                                        const newValues = e.target.checked
                                            ? [...(filters?.[config.stateKey] || []), option.id]
                                            : (filters?.[config.stateKey] || []).filter((id) => id !== option.id);
                                        handleFilterChange({ [config.stateKey]: newValues });
                                    }}
                                    id={`${config.accessor}-${option.id}`}
                                />
                                <label className="form-check-label" htmlFor={`${config.accessor}-${option.id}`}>
                                    {option.name} <span className="text-muted">({option.count || 0})</span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
                {options.length > 6 && (
                    <button
                        type="button"
                        className="btn btn-link text-primary p-0 mt-2"
                        onClick={() =>
                            setShowAllStates((prev) => ({
                                ...prev,
                                [config.accessor]: !prev[config.accessor],
                            }))
                        }
                    >
                        {showAllStates[config.accessor] ? (
                            <>
                                Show Less <i className="bi bi-chevron-up ms-1"></i>
                            </>
                        ) : (
                            <>
                                Show More <i className="bi bi-chevron-down ms-1"></i>
                            </>
                        )}
                    </button>
                )}
            </div>
        );
    };


    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            const updated = { ...prev, ...newFilters };
            if (updated.categories) {
                updated.categories = [...new Set(updated.categories)];
            } else {
                updated.categories = [];
            }
            if (updated.subcategories) {
                updated.subcategories = [...new Set(updated.subcategories)];
            } else {
                updated.subcategories = [];
            }
            if (updated.colors) {
                updated.colors = [...new Set(updated.colors)];
            } else {
                updated.colors = [];
            }
            if (updated.brands) {
                updated.brands = [...new Set(updated.brands)];
            } else {
                updated.brands = [];
            }
            return updated;
        });
    }, [setFilters]);

    // Update filters when price range changes
    useEffect(() => {
        if (selectedPriceRange !== filters?.priceRange) {
            handleFilterChange({ priceRange: selectedPriceRange });
        }
    }, [selectedPriceRange, filters, handleFilterChange]);

    // Show skeleton if filterData is not available
    if (!filterData) {
        return <FilterSkeleton />;
    }

    // Check if filterData has any valid filter sections
    const hasValidFilterData = Object.values(FILTER_CONFIG[filterType]).some(
        (config) => Array.isArray(filterData[config.accessor]) && filterData[config.accessor].length > 0
    ) || (Array.isArray(priceRanges) && priceRanges.length > 0);

    // Show skeleton if no valid filter data
    if (!hasValidFilterData) {
        return <FilterSkeleton />;
    }

    return (
        <div className="bg-white w-100 p-3 rounded max-h-[calc(100vh-120px)] overflow-y-auto mb-3 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="fw-bold fs-5 m-0">Filters</h2>
                <button
                    className="btn btn-link text-muted p-1"
                    onClick={handleReset}
                    disabled={!hasActiveFilters}
                    title="Reset all filters"
                >
                    <RotateCcw size={20} />
                </button>
            </div>

            {/* Price Range */}
            {priceRanges.length > 0 && (
                <div className="mb-4">
                    <h3 className="fw-bold mb-2 fs-6">PRICE RANGE</h3>
                    <ul className="list-unstyled">
                        {priceRanges.map((range, index) => (
                            <li key={index} className="form-check mb-1">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="priceRange"
                                    checked={
                                        selectedPriceRange &&
                                        selectedPriceRange.min === range.min &&
                                        selectedPriceRange.max === range.max
                                    }
                                    onChange={() => {
                                        setSelectedPriceRange({ min: range.min, max: range.max });
                                    }}
                                    id={`price-range-${range.min}-${range.max}`}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`price-range-${range.min}-${range.max}`}
                                >
                                    ₹{range.min.toLocaleString()} - ₹{range.max.toLocaleString()}{" "}
                                    <span className="text-muted">({range.count || 0})</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Render all filter sections */}
            {Object.values(FILTER_CONFIG[filterType]).map((config) => (
                <React.Fragment key={config.accessor}>
                    {renderFilterSection(config)}
                </React.Fragment>
            ))}

            {/* Apply Button for Mobile */}
            {isMobile && (
                <div className="bg-white p-3 border-top position-fixed start-0 end-0 bottom-0">
                    <button className="btn btn-primary w-100" onClick={handleApply}>
                        Apply Filter
                    </button>
                </div>
            )}
        </div>
    );
};

export default function FilterSidebar(props) {
    const [open, setOpen] = useState(false);

    const handleReset = () => {
        if (props.onReset) {
            props.onReset();
        }
    };

    return (
        <>
            {/* Desktop View */}
            <aside className="d-none h-100 d-md-block col-3" style={{
                maxWidth: "300px"
            }}>
                <div style={{
                    maxWidth: "300px",
                    height: "100%",
                    position: "sticky",
                    top: "0",
                    padding: "0px 10px 10px 10px"
                }}>
                    <FilterContent {...props} setOpen={setOpen} onReset={handleReset} />
                </div>
            </aside>

            {/* Mobile View */}
            <div className="d-md-none">
                {open && (
                    <div className="filter-popup position-fixed top-0 start-0 h-100 w-100 bg-white d-flex flex-column overflow-y-auto">
                        <div className="p-3 text-white position-sticky top-0 d-flex align-items-center gap-2"
                            style={{
                                background: "var(--PRIMARY_COLOR)",
                            }}
                        >
                            <button
                                className="btn p-0 text-white"
                                onClick={() => setOpen(false)}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h5 className="m-0">Filters</h5>
                        </div>
                        <FilterContent {...props} isMobile={true} setOpen={setOpen} onReset={handleReset} />
                    </div>
                )}
            </div>
            <div
                className={`${styles.productBtnBox} hideInDesktop d-inline-flex align-items-stretch col-12 position-fixed bottom-0 start-0`}
            >
                <span
                    className={`${styles.goCartBtn} position-relative col-6 d-inline-flex align-items-center justify-content-center gap-2`}
                    onClick={() => setOpen(true)}
                >
                    {" "}
                    <SortByIcon />
                    Sort By
                </span>
                <span
                    className={`${styles.AddCartBtn} position-relative col-6 d-inline-flex align-items-center justify-content-center gap-2`}
                    onClick={() => setOpen(true)}
                >
                    <FilterIcon />
                    Filters
                </span>
            </div>
        </>
    );
};